import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { Company, User } from '../api/supabaseEntities.js';
import { supabase } from '../api/supabaseClient.js';
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Label } from "../components/ui/label.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { ArrowLeft, UserPlus, User as UserIcon, Save, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from "../components/ui/alert.jsx";

export default function NewUser() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const isEditing = Boolean(userId);
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
    role: 'client',
    department: '',
    position: '',
    phone: '',
    company_id: '',
    status: 'active',
    notes: '',
    avatar_url: '',
    is_admin: false,
    preferences: {},
    permissions: {
      tickets: {
        create: true,
        read: true,
        update: false,
        delete: false
      },
      users: {
        create: false,
        read: false,
        update: false,
        delete: false
      },
      companies: {
        create: false,
        read: false,
        update: false,
        delete: false
      },
      reports: {
        read: false,
        export: false
      },
      settings: {
        read: false,
        update: false
      }
    }
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState('');
  
  useEffect(() => {
    loadData();
  }, [userId]);

  const loadData = async () => {
    try {
      // Verifica permissões do usuário atual
      const userData = await User.me();
      setCurrentUser(userData);
      
      // Apenas usuários internos podem criar/editar usuários
      if (userData.role === 'client') {
        navigate(createPageUrl('Dashboard'));
        return;
      }
      
      // Carrega empresas para seleção
      const companiesData = await Company.list();
      setCompanies(companiesData);

      // Se for edição, carrega dados do usuário
      if (isEditing && userId) {
        try {
          const userToEdit = await User.findById(userId);
          
          // Garantir que todos os campos sejam strings definidas para evitar warning de componente controlado
          setFormData({
            email: userToEdit.email || '',
            name: userToEdit.name || '',
            password: '',
            confirmPassword: '',
            role: userToEdit.role || 'client',
            company_id: userToEdit.company_id || '',
            avatar_url: userToEdit.avatar_url || '',
            is_admin: userToEdit.is_admin || false,
            preferences: userToEdit.preferences || {}
          });
        } catch (error) {
          console.error('Error loading user:', error);
          setError('Usuário não encontrado.');
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Erro ao carregar dados. Tente novamente.');
    }

  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      // Validações
      if (!formData.email || !formData.name) {
        setError('Email e nome completo são obrigatórios.');
        setIsLoading(false);
        return;
      }

      if (!isEditing) {
        if (!formData.password || formData.password.length < 6) {
          setError('A senha deve ter pelo menos 6 caracteres.');
          setIsLoading(false);
          return;
        }

        if (formData.password !== formData.confirmPassword) {
          setError('As senhas não coincidem.');
          setIsLoading(false);
          return;
        }

        // Verificar se o email já existe usando função RPC mais robusta
        try {
          console.log('🔍 Verificando se email já existe:', formData.email);
          
          // Primeira tentativa: usar função RPC específica
          try {
            const { data: isUnique, error: rpcError } = await supabase.rpc('check_email_unique', {
              p_email: formData.email
            });
            
            if (!rpcError && isUnique === false) {
              console.log('❌ Email duplicado encontrado via RPC');
              setError('Este email já está cadastrado no sistema.');
              setIsLoading(false);
              return;
            } else if (!rpcError && isUnique === true) {
              console.log('✅ Email único confirmado via RPC');
            } else if (rpcError) {
              console.warn('⚠️ RPC check_email_unique falhou, usando fallback:', rpcError);
              throw rpcError; // Ir para fallback
            }
          } catch (rpcError) {
            // Fallback: usar busca tradicional mas mais específica
            console.log('🔄 Fallback: verificando email via busca direta...');
            const { data: existingUsers, error: searchError } = await supabase
              .from('user_profiles')
              .select('id, email')
              .ilike('email', formData.email); // Case insensitive
            
            if (searchError) {
              console.warn('⚠️ Erro na busca de email, permitindo cadastro:', searchError);
            } else if (existingUsers && existingUsers.length > 0) {
              console.log('❌ Email duplicado encontrado via busca direta:', existingUsers);
              setError(`Este email já está cadastrado no sistema. Encontrados ${existingUsers.length} registro(s).`);
              setIsLoading(false);
              return;
            } else {
              console.log('✅ Email único confirmado via busca direta');
            }
          }
        } catch (error) {
          // Se houver erro crítico, permitir cadastro com aviso
          console.warn('⚠️ Erro crítico na verificação de email, permitindo cadastro:', error);
          console.log('⚠️ ATENÇÃO: Não foi possível verificar duplicação de email');
        }
      }

      // Preparar dados para envio (apenas campos válidos da tabela user_profiles)
      const userData = {
        email: formData.email,
        name: formData.name,
        phone: formData.phone || null,
        role: formData.role,
        company_id: formData.company_id || null,
        is_admin: formData.role === 'admin'
      };

      // Incluir password apenas para novos usuários
      if (!isEditing && formData.password) {
        userData.password = formData.password;
      }

      // Filtrar campos não definidos para evitar inserções desnecessárias
      Object.keys(userData).forEach(key => {
        if (userData[key] === undefined || userData[key] === '') {
          delete userData[key];
        }
      });
      
      console.log('📋 Dados do usuário a serem enviados:', userData);

      if (isEditing) {
        await User.update(userId, userData);
      } else {
        await User.create(userData);
      }
      
      setSuccess(true);
      
      // Redirecionar após sucesso
      setTimeout(() => {
        navigate(createPageUrl('Users'));
      }, 2000);
      
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      setError('Erro ao salvar usuário. Verifique os dados e tente novamente.');
    }
    
    setIsLoading(false);
  };

  if (!currentUser || currentUser.role === 'client') {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(createPageUrl('Users'))}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">
            {isEditing ? 'Editar Usuário' : 'Novo Usuário'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isEditing ? 'Edite as informações do usuário' : 'Cadastre um novo usuário no sistema'}
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {success && (
        <Alert className="bg-green-50 text-green-800 border-green-200">
          <Save className="h-4 w-4" />
          <AlertDescription>
            {isEditing ? 'Usuário atualizado com sucesso!' : 'Usuário cadastrado com sucesso!'}
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-1">
          <TabsTrigger value="basic">Informações Básicas</TabsTrigger>
        </TabsList>

        <TabsContent value="basic">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                {isEditing ? 'Editar Usuário' : 'Novo Usuário'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome Completo *</Label>
                    <Input
                      id="name"
                      placeholder="Nome completo do usuário"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">E-mail *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="email@exemplo.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      disabled={isEditing}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefone/WhatsApp</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(11) 99999-9999"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                    />
                  </div>

                  {!isEditing && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="password">Senha *</Label>
                        <Input
                          id="password"
                          type="password"
                          placeholder="Mínimo 6 caracteres"
                          value={formData.password}
                          onChange={(e) => handleChange('password', e.target.value)}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword">Confirmar Senha *</Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          placeholder="Confirme a senha"
                          value={formData.confirmPassword}
                          onChange={(e) => handleChange('confirmPassword', e.target.value)}
                          required
                        />
                      </div>
                    </>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="role">Perfil *</Label>
                    <Select
                      value={formData.role}
                      onValueChange={(value) => {
                        handleChange('role', value);
                        // Limpa company_id se não for cliente
                        handleChange('company_id', value === 'client' ? formData.company_id : '');
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o perfil" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client">Cliente</SelectItem>
                        <SelectItem value="support">Suporte</SelectItem>
                        <SelectItem value="dev">Desenvolvedor</SelectItem>
                        <SelectItem value="manager">Gerente</SelectItem>
                        <SelectItem value="admin">Administrador</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.role === 'client' && (
                    <div className="space-y-2">
                      <Label htmlFor="company">Empresa *</Label>
                      <Select
                        value={formData.company_id}
                        onValueChange={(value) => handleChange('company_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a empresa" />
                        </SelectTrigger>
                        <SelectContent>
                          {companies.map((company) => (
                            <SelectItem key={company.id} value={company.id}>
                              {company.name || company.corporate_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(createPageUrl('Users'))}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isLoading} className="bg-primary">
                    {isLoading ? 'Salvando...' : isEditing ? 'Atualizar' : 'Cadastrar'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
